import { AxiosResponse } from "axios"
import { HTTP_UNAUTHORIZED, HTTP_CONFLICT, HTTP_BAD_REQUEST, HTTP_FORBIDDEN } from "./statusCodes"

export const isBadRequestError = (error: unknown): error is AxiosResponse<{message: string}> => 
    (error as AxiosResponse<unknown>)?.status === HTTP_BAD_REQUEST

export const isUnauthorizedError = (error: unknown): error is AxiosResponse<{message: string}> => 
    (error as AxiosResponse<unknown>)?.status === HTTP_UNAUTHORIZED

export const isConflictError = <T = unknown>(error: unknown): error is AxiosResponse<T> => 
    (error as AxiosResponse<unknown>)?.status === HTTP_CONFLICT
    
export const isForbiddenError = (error: unknown): error is AxiosResponse<{message: string}> => 
    (error as AxiosResponse<unknown>)?.status === HTTP_FORBIDDEN

export const isCancelledError = (error: unknown) =>
    (error as any)?.code === 'ERR_CANCELED' || (error as any)?.message === 'canceled'

/**
 * Extracts error message from API response.
 * The axios interceptor in requests.prod.ts unwraps error.response, so we receive the response object directly.
 * Handles both string responses (e.g., BadRequest("message")) and object responses with message properties.
 */
export const getErrorMessage = (error: unknown): string | undefined => {
    if (!error || typeof error !== 'object') {
        return typeof error === 'string' ? error : undefined;
    }

    // The axios interceptor unwraps error.response, so check error.data first
    const unwrappedError = error as { data?: string | { message?: string; Message?: string }, status?: number };

    // Check if error.data exists (this is what the interceptor returns)
    if (unwrappedError.data !== undefined) {
        const errorData = unwrappedError.data;
        // If data is a string, return it directly (BadRequest with string message)
        if (typeof errorData === 'string') {
            return errorData;
        }
        // Otherwise check for message properties
        if (typeof errorData === 'object' && errorData !== null) {
            return errorData?.message ?? errorData?.Message;
        }
    }

    // Fallback: check for axios error structure: error.response.data (in case interceptor didn't run)
    const axiosError = error as { response?: { data?: string | { message?: string; Message?: string } } };
    if (axiosError.response?.data) {
        const responseData = axiosError.response.data;
        if (typeof responseData === 'string') {
            return responseData;
        }
        if (typeof responseData === 'object' && responseData !== null) {
            return responseData?.message ?? responseData?.Message;
        }
    }

    return undefined;
}
